import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import type { IPayrollSheetApprovalAccessKey } from "@/payroll/payroll.d";
import { ApprovalToggle } from "./approval-toggle";
import { APPROVAL_GATES } from "./approval-types";
import type { ApprovalPanelProps } from "./types";
import { describeApprovalProvenance, getSheetSummary } from "./utils";

/**
 * Sign-off for one unit — what the workbook toolbar opens. With a single unit
 * there is room to spell each gate out, so the two decisions get their names and
 * a line explaining what they mean.
 */
export const SheetApprovalPanel = ({
  accessKey,
  pendingApproval,
  onApprovalChange,
}: ApprovalPanelProps & { accessKey: IPayrollSheetApprovalAccessKey }) => {
  const sheetSummary = getSheetSummary(accessKey);

  return (
    <Stack gap="none" align="stretch" fullWidth className="min-w-0">
      <Stack
        gap="xs"
        align="stretch"
        fullWidth
        className="min-w-0 border-b border-stroke-tertiary px-4 py-3"
      >
        <Typography variant="subtitleW3" className="truncate">
          {accessKey.name}
        </Typography>
        {sheetSummary && (
          <Typography
            variant="captionW1"
            textColor="tertiary"
            className="truncate"
          >
            {sheetSummary}
          </Typography>
        )}
      </Stack>

      {APPROVAL_GATES.map((gate) => (
        <Stack
          key={gate.type}
          direction="row"
          align="center"
          justify="between"
          gap="md"
          fullWidth
          className="border-b border-stroke-tertiary px-4 py-3 last:border-b-0"
        >
          <Stack gap="none" className="min-w-0">
            <Typography variant="bodyW3" className="font-semibold">
              {gate.label}
            </Typography>
            <Typography variant="captionW1" textColor="tertiary">
              {describeApprovalProvenance(accessKey.approvals[gate.type]) ??
                gate.description}
            </Typography>
          </Stack>
          <ApprovalToggle
            gate={gate}
            accessKey={accessKey}
            isBusy={Boolean(pendingApproval)}
            isPending={
              pendingApproval?.accessKeyId === accessKey.id &&
              pendingApproval.approvalType === gate.type
            }
            onChange={
              onApprovalChange
                ? (enabled) => onApprovalChange(accessKey, gate.type, enabled)
                : undefined
            }
          />
        </Stack>
      ))}

      {!accessKey.approval_authorized && (
        <Typography
          variant="captionW1"
          textColor="tertiary"
          className="border-t border-stroke-tertiary px-4 py-2.5"
        >
          You can see these sheets, but you can&apos;t approve them.
        </Typography>
      )}
    </Stack>
  );
};
